// Product entitlements — single source of truth (frontend).
//
// A company can subscribe to only some product modules. Everything else is CORE
// (always available). This file drives all three synchronized layers of the
// frontend: (1) nav hides disabled items, (2) the ModuleGuard redirects if the
// current path's module is disabled. The backend mirrors this taxonomy in
// middleware/requireModule.js.
//
// IMPORTANT: keep ROUTE_MODULES in sync with the backend route → module gating.

// The gatable product modules. Anything NOT covered here is CORE (always on).
export const MODULES = ['finance', 'guestExperience', 'operations', 'inbox'];

// Dashboard route (path prefix) → module.
// Any dashboard path NOT listed here is CORE and always allowed
// (e.g. /dashboard, /dashboard/calendar, /dashboard/reservations,
// /dashboard/units, /dashboard/settings, /dashboard/updates, /dashboard/notifications).
export const ROUTE_MODULES = {
  // Finance
  '/dashboard/financials': 'finance',
  '/dashboard/expenses': 'finance',
  '/dashboard/owner-payouts': 'finance',
  '/dashboard/owners': 'finance',
  '/dashboard/fee-management': 'finance',

  // Guest Experience
  '/dashboard/checkin-builder': 'guestExperience',
  '/dashboard/guest-app-studio': 'guestExperience',
  '/dashboard/upsells': 'guestExperience',
  '/dashboard/payments': 'guestExperience',
  '/dashboard/data': 'guestExperience',

  // Operations
  '/dashboard/operations': 'operations',
  '/dashboard/cleaner-calendar': 'operations',
  '/dashboard/checklists': 'operations',

  // Inbox
  '/dashboard/chatbot': 'inbox',
  '/dashboard/luna': 'inbox',
};

// Notification category → module. Categories not listed are CORE (reservations…).
// Used by useNotifications (bell items + counters) and NotificationsSettings
// (preference toggles) so a company never sees notifications for modules it
// didn't purchase.
export const NOTIFICATION_CATEGORY_MODULES = {
  payments: 'guestExperience',
  services: 'guestExperience',
  deposits: 'guestExperience', // guest security deposits (Stripe) → GX
  operations: 'operations',
  luna: 'inbox',
};

// Type-level extras: GX-flavored types that live under a CORE category
// (e.g. guest check-in events are filed under 'reservations').
export const NOTIFICATION_TYPE_MODULES = {
  guest_open: 'guestExperience',
  guest_id_uploaded: 'guestExperience',
  checkin_completed: 'guestExperience',
  guest_review: 'guestExperience',
  service_pending_approval: 'guestExperience',
  luna_escalation: 'inbox',
  luna_proposal: 'inbox',
  luna_case_concluded: 'inbox',
  guest_review_received: 'inbox',
};

/** Module a notification belongs to (null = core, always shown). */
export function moduleForNotification(n) {
  return NOTIFICATION_TYPE_MODULES[n?.type] || NOTIFICATION_CATEGORY_MODULES[n?.category] || null;
}

/**
 * Resolve the product module a dashboard path belongs to.
 * Uses longest-prefix matching so nested paths (e.g. /dashboard/owners/123)
 * inherit their parent's module. Returns null for CORE paths (always allowed).
 *
 * @param {string} pathname
 * @returns {string|null} module name, or null if the path is core/unmapped
 */
export function moduleForPath(pathname) {
  if (!pathname) return null;

  let bestMatch = null;
  let bestLength = -1;

  for (const route of Object.keys(ROUTE_MODULES)) {
    // Prefix match on a path boundary: exact route, or route followed by '/'.
    if (pathname === route || pathname.startsWith(route + '/')) {
      if (route.length > bestLength) {
        bestLength = route.length;
        bestMatch = ROUTE_MODULES[route];
      }
    }
  }

  return bestMatch;
}
